import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import type { ActionAvailability } from "@amber/contracts";
import { Button, Drawer, isApiError, errorText } from "@amber/ui";
import type { SourceDetail } from "../../contracts/index";
import { sourceQuery } from "../queries";
import { SourceDialog, type DialogKind } from "./SourceDialogs";
import {
  CoverageSection,
  HistorySection,
  ScansSection,
  SettingsSection,
  SourceSummary,
  YearsSection,
} from "./SourceSections";
import { ValueSection } from "./SourceValueSection";

function ActionButton({
  availability,
  label,
  onClick,
}: {
  availability: ActionAvailability;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button size="sm" disabledReason={availability.enabled ? null : availability.reason} onClick={onClick}>
      {label}
    </Button>
  );
}

export function SourceDrawer({
  id,
  onClose,
  onGaps,
  onEdit,
}: {
  id: string;
  onClose: () => void;
  onGaps: () => void;
  onEdit: () => void;
}) {
  const query = useQuery(sourceQuery(id));
  const [dialog, setDialog] = useState<DialogKind | null>(null);
  const detail = query.data;
  if (!detail) {
    return (
      <Drawer title={query.isError ? "Source not found" : "Loading source…"} onClose={onClose}>
        {query.isError ? (
          <p className="m-0 text-muted">
            {isApiError(query.error) && query.error.status === 404
              ? "No source with this ID, or it isn't visible to your role."
              : errorText(query.error)}
          </p>
        ) : null}
      </Drawer>
    );
  }
  const s = detail.source;
  return (
    <Drawer
      title={s.title}
      subtitle={`${s.platform.label} · ${s.kind_label}`}
      onClose={onClose}
      footer={<SourceActions detail={detail} onDialog={setDialog} onEdit={onEdit} />}
    >
      <div className="flex flex-col gap-4 text-ui">
        <SourceSummary detail={detail} />
        <CoverageSection detail={detail} onGaps={onGaps} />
        <YearsSection detail={detail} />
        <ScansSection detail={detail} />
        <ValueSection sourceId={s.id} />
        <SettingsSection detail={detail} />
        <HistorySection detail={detail} />
      </div>
      <SourceDialog
        dialog={dialog}
        detail={detail}
        onClose={() => {
          setDialog(null);
        }}
      />
    </Drawer>
  );
}

function SourceActions({
  detail,
  onDialog,
  onEdit,
}: {
  detail: SourceDetail;
  onDialog: (kind: DialogKind) => void;
  onEdit: () => void;
}) {
  const s = detail.source;
  const paused = s.health.state === "paused";
  return (
    <span className="flex flex-wrap items-center gap-2">
      <ActionButton
        availability={detail.actions.scan_now}
        label="Scan now…"
        onClick={() => {
          onDialog("scan");
        }}
      />
      {paused ? (
        <ActionButton
          availability={detail.actions.resume}
          label="Resume…"
          onClick={() => {
            onDialog("resume");
          }}
        />
      ) : (
        <ActionButton
          availability={detail.actions.pause}
          label="Pause…"
          onClick={() => {
            onDialog("pause");
          }}
        />
      )}
      {s.health.state === "unusually_quiet" ? (
        <ActionButton
          availability={detail.actions.mark_quiet}
          label="Mark as expected quiet…"
          onClick={() => {
            onDialog("quiet");
          }}
        />
      ) : null}
      <ActionButton availability={detail.actions.edit} label="Edit source…" onClick={onEdit} />
      {s.people.some((p) => p.relationship === "office") ? (
        <ActionButton
          availability={detail.actions.monitor}
          label="Change account holder…"
          onClick={() => {
            onDialog("holder");
          }}
        />
      ) : null}
      {s.people.length === 0 || s.shared_plan ? (
        <ActionButton
          availability={detail.actions.monitor}
          label={s.shared_plan ? "Edit shared-source plan…" : "Monitor this source…"}
          onClick={() => {
            onDialog("monitor");
          }}
        />
      ) : null}
      <a
        href={s.url}
        target="_blank"
        rel="noreferrer noopener"
        className="ml-auto inline-flex items-center gap-1 text-md text-link"
      >
        Original
        <ExternalLink size={12} aria-hidden="true" />
      </a>
    </span>
  );
}
